import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { SECTORS } from "../data/content";
import { SECTOR_DETAILS } from "../data/sectorDetails";
import { Search, ArrowUpRight } from "lucide-react";

// Map sector.id → detail slug for "View detail" link
const SECTOR_ID_TO_SLUG = Object.values(SECTOR_DETAILS).reduce((acc, d) => {
  acc[d.sectorId] = d.slug;
  return acc;
}, {});

const CATEGORIES = ["Tümü", ...Array.from(new Set(SECTORS.map((s) => s.category)))];

export default function Sectors() {
  const [active, setActive] = useState("Tümü");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  const filtered = useMemo(() => {
    return SECTORS.filter((s) => {
      const matchesCat = active === "Tümü" || s.category === active;
      const q = query.trim().toLowerCase();
      const matchesQ =
        !q ||
        s.title.toLowerCase().includes(q) ||
        s.summary.toLowerCase().includes(q) ||
        s.category.toLowerCase().includes(q);
      return matchesCat && matchesQ;
    });
  }, [active, query]);

  return (
    <div data-testid="page-sectors" className="relative">
      <div className="absolute inset-x-0 top-0 h-[300px] bg-grid opacity-30 pointer-events-none" />

      <section className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-12">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange mb-4">
          / Sektörler — 08 Uygulama Alanı
        </div>
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-[1.05] max-w-4xl">
          Her sektör için özelleşmiş <span className="thermal-text">termal güvenlik</span> mimarisi.
        </h1>
        <p className="mt-6 max-w-2xl text-slate-300 text-lg leading-relaxed">
          GES&apos;ten orman yangınlarına, tünelden balık çiftliklerine — kart üzerinde gezdirin,
          termal taramayı görün.
        </p>

        {/* Filter bar */}
        <div className="mt-10 flex flex-col md:flex-row gap-3 md:items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                data-testid={`filter-${cat}`}
                onClick={() => setActive(cat)}
                className={`font-mono text-[11px] uppercase tracking-[0.18em] px-4 py-2 border transition-colors ${
                  active === cat
                    ? "bg-thermal-orange text-white border-thermal-orange"
                    : "border-white/15 text-slate-300 hover:border-thermal-orange hover:text-thermal-orange"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="relative md:w-72">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="sector-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Sektör ara..."
              className="w-full bg-[#0D131F] border border-white/15 focus:border-thermal-orange outline-none text-white text-sm pl-10 pr-4 py-2.5 font-mono placeholder:text-slate-600"
            />
          </div>
        </div>

        <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
          / {filtered.length} sonuç bulundu
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence mode="popLayout">
            {filtered.map((s, i) => (
              <motion.button
                key={s.id}
                layout
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.35, delay: (i % 6) * 0.04 }}
                onClick={() => setSelected(s)}
                data-testid={`sector-card-${s.id}`}
                className="thermal-card text-left bg-[#0D131F] border border-white/10 hover:border-thermal-orange/50 aspect-[4/5] relative group"
                style={{
                  backgroundImage: `url(${s.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                <div className="thermal-overlay" />
                <div className="relative z-10 h-full flex flex-col justify-between p-6">
                  <div className="flex items-start justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-thermal-orange bg-[#06090F]/70 px-2 py-1 backdrop-blur">
                      / {s.category}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">
                      0{SECTORS.indexOf(s) + 1}
                    </span>
                  </div>

                  <div>
                    <div className="font-mono text-thermal-yellow text-sm mb-1">
                      {s.metric.value} <span className="text-white/50">— {s.metric.label}</span>
                    </div>
                    <h3 className="text-2xl font-bold text-white leading-tight group-hover:text-thermal-yellow transition-colors">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-slate-300 text-sm line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      {s.summary}
                    </p>
                  </div>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-20 border border-dashed border-white/15">
            <div className="font-mono text-thermal-orange uppercase text-xs tracking-[0.2em]">
              / no_signal
            </div>
            <div className="mt-2 text-slate-400">Sonuç bulunamadı, başka anahtar kelime deneyin.</div>
          </div>
        )}
      </section>

      {/* Detail Modal */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setSelected(null)}
            data-testid="sector-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-3xl w-full bg-[#0D131F] border border-thermal-orange/40 max-h-[90vh] overflow-auto"
            >
              <div
                className="h-56 md:h-72 relative"
                style={{
                  backgroundImage: `url(${selected.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D131F] via-[#0D131F]/40 to-transparent" />
                <button
                  onClick={() => setSelected(null)}
                  data-testid="modal-close"
                  className="absolute top-4 right-4 font-mono text-xs uppercase tracking-[0.2em] bg-[#06090F]/80 border border-white/20 hover:border-thermal-orange hover:text-thermal-orange px-3 py-1.5"
                >
                  Kapat ×
                </button>
                <div className="absolute bottom-5 left-6 right-6">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange">
                    / {selected.category}
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold mt-1">{selected.title}</h2>
                </div>
              </div>
              <div className="p-6 md:p-8">
                <p className="text-slate-300 leading-relaxed text-lg">{selected.summary}</p>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {selected.bullets.map((b, i) => (
                    <div
                      key={i}
                      className="flex gap-3 border border-white/10 p-4 bg-[#06090F]/50"
                    >
                      <span className="font-mono text-thermal-orange text-xs">0{i + 1}</span>
                      <span className="text-slate-300 text-sm leading-relaxed">{b}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 inline-flex items-center gap-3 border border-thermal-orange/40 px-4 py-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                    Anahtar Metrik
                  </div>
                  <div className="font-mono text-2xl text-thermal-yellow font-semibold">
                    {selected.metric.value}
                  </div>
                  <div className="text-sm text-slate-300">— {selected.metric.label}</div>
                </div>

                {SECTOR_ID_TO_SLUG[selected.id] && (
                  <Link
                    to={`/sektorler/${SECTOR_ID_TO_SLUG[selected.id]}`}
                    onClick={() => setSelected(null)}
                    data-testid="modal-detail-link"
                    className="mt-6 inline-flex items-center justify-center gap-2 bg-thermal-orange hover:bg-thermal-red text-white px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors min-h-[48px]"
                  >
                    Detaylı Sayfayı Aç <ArrowUpRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
